import {
  getCanonicalPredicateForAlias,
  getRelationPredicateDefinition,
} from '../../../core/ontology/RelationPredicateRegistry';
import { fieldSensitivityRank } from '../../../core/fields/security';
import type { FieldVisibilityConfig } from './fieldVisibility';

/**
 * Link-viewer snapshots are readable without an authenticated principal, so
 * their durable payload is always public regardless of the publisher's own
 * clearance. Authenticated sharing is a separate live-projection contract.
 */
export const PUBLIC_VIEWER_FIELD_VISIBILITY: FieldVisibilityConfig = Object.freeze({
  sensitivityLevel: 'public',
});

/**
 * Org-chart presentation connectors that carry the published hierarchy.
 *
 * Ontology marks `reports_to` existence as `internal` to prevent ambient
 * leakage from folder projections. Publishing an org viewer link is an
 * explicit disclosure of that hierarchy, so these presentation edges are
 * allowed in anonymous snapshots. Succession / capacity / assignment
 * relations stay on the ontology sensitivity path below.
 */
const ORG_STRUCTURE_EDGE_TYPES = new Set([
  'reporting',
  'reports_to',
]);

const ORG_STRUCTURE_PREDICATES = new Set([
  'reporting',
  'reports_to',
]);

export function isPublicViewerEdge(edge: unknown): boolean {
  const semanticPredicate = relationPredicate(edge);
  if (isOrgStructurePresentationEdge(edge, semanticPredicate)) return true;
  if (!semanticPredicate && presentationEdgeType(edge) !== null) return true;
  const predicate = semanticPredicate ?? edgeType(edge);
  if (!predicate) return false;

  const migratedPredicate = predicate === 'successor_for' ? 'successor_of' : predicate;
  const canonicalPredicate = getCanonicalPredicateForAlias(migratedPredicate) ?? migratedPredicate;
  const definition = getRelationPredicateDefinition(canonicalPredicate);

  // A semantic marker with no registered predicate is not a presentation
  // connector. Fail closed so newly introduced relations cannot become public
  // merely because viewer policy has not learned their classification yet.
  if (!definition) return false;
  if (!definition.existenceSensitivity) return true;
  return fieldSensitivityRank(definition.existenceSensitivity) <= fieldSensitivityRank('public');
}

function isOrgStructurePresentationEdge(
  edge: unknown,
  semanticPredicate: string | undefined,
): boolean {
  const type = edgeType(edge);
  const isStructureType = type !== null && ORG_STRUCTURE_EDGE_TYPES.has(type);
  const isStructurePredicate = !!semanticPredicate && ORG_STRUCTURE_PREDICATES.has(semanticPredicate);
  if (!isStructureType && !isStructurePredicate) return false;

  // A reporting-typed connector overlaid with a private relation (e.g.
  // succession) must still fail closed.
  if (semanticPredicate && !ORG_STRUCTURE_PREDICATES.has(semanticPredicate)) {
    const migratedPredicate = semanticPredicate === 'successor_for' ? 'successor_of' : semanticPredicate;
    const canonicalPredicate = getCanonicalPredicateForAlias(migratedPredicate) ?? migratedPredicate;
    const definition = getRelationPredicateDefinition(canonicalPredicate);
    if (!definition) return false;
    if (!definition.existenceSensitivity) return true;
    return fieldSensitivityRank(definition.existenceSensitivity) <= fieldSensitivityRank('public');
  }

  return true;
}

function relationPredicate(edge: unknown): string | undefined {
  if (!edge || typeof edge !== 'object') return undefined;
  const record = edge as Record<string, unknown>;
  const attrs = objectRecord(record.attrs);
  const props = objectRecord(record.props);
  const storedSemantics = objectRecord(attrs?.__relationSemantics);
  const candidates = [
    storedSemantics?.predicate,
    attrs?.__relationshipSubtype,
    // Prefer the ontology predicate when both relation label and predicate exist
    // (live charts store relation: 'reporting' alongside predicate: 'reports_to').
    attrs?.predicate,
    attrs?.relation,
    props?.relation,
    props?.predicate,
    record.relationshipType,
    record.predicate,
    record.kind,
  ];
  return candidates.find((value): value is string => typeof value === 'string' && value.length > 0);
}

const PUBLIC_PRESENTATION_EDGE_TYPES = new Set([
  'line',
  'orthogonal',
  'elbow',
  'curve',
  'bezier',
  'presentation-line',
]);

function presentationEdgeType(edge: unknown): string | null {
  const type = edgeType(edge);
  return typeof type === 'string' && PUBLIC_PRESENTATION_EDGE_TYPES.has(type) ? type : null;
}

function edgeType(edge: unknown): string | null {
  if (!edge || typeof edge !== 'object') return null;
  const type = (edge as Record<string, unknown>).type;
  return typeof type === 'string' && type.length > 0 ? type : null;
}

function objectRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}
